import { APP_URL, CHABAD_APP_URL } from '@/utils/const';

// The apps an organization's people can sign in to: Screenkode, Chabad Digital
// or both — never none. The backend reads them from the organization's modules
// and sends them as `organization.apps`, in the order below.

export type OrganizationApp = 'screenkode' | 'chabad_digital';

export interface OrganizationAppInfo {
  value: OrganizationApp;
  name: string;
  /** Where its people sign in, e.g. https://app.chabaddigital.com */
  url: string;
  /** The same without the scheme, to show. */
  host: string;
}

const hostOf = (url: string) => url.replace(/^https?:\/\//, '');

export const ORGANIZATION_APPS: OrganizationAppInfo[] = [
  { value: 'screenkode', name: 'Screenkode', url: APP_URL, host: hostOf(APP_URL) },
  { value: 'chabad_digital', name: 'Chabad Digital', url: CHABAD_APP_URL, host: hostOf(CHABAD_APP_URL) },
];

/** An app's name and address, or null for none. */
export function organizationApp(value?: string | null): OrganizationAppInfo | null {
  return ORGANIZATION_APPS.find((app) => app.value === value) || null;
}

/** These apps, each once and in the usual order — whatever order they came in. */
export function appsIn(values: Array<string | null | undefined>): OrganizationAppInfo[] {
  return ORGANIZATION_APPS.filter((app) => values.includes(app.value));
}

/**
 * The apps an organization's people can sign in to. From a server older than
 * `apps`, its one `app` — or both, which is what no app meant there.
 */
export function organizationApps(
  organization?: { apps?: string[] | null; app?: string | null } | null
): OrganizationAppInfo[] {
  if (!organization) return [];
  if (Array.isArray(organization.apps)) return appsIn(organization.apps);
  return organization.app ? appsIn([organization.app]) : [...ORGANIZATION_APPS];
}

/** Words in a list: "a", "a and b", "a, b and c". */
export function listOf(words: string[], joiner: 'and' | 'or' = 'and'): string {
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} ${joiner} ${words[words.length - 1]}` : words[0] || '';
}

/** "Screenkode", or "Screenkode and Chabad Digital". */
export function appNames(apps: OrganizationAppInfo[], joiner: 'and' | 'or' = 'and'): string {
  return listOf(apps.map((app) => app.name), joiner);
}

/** "app.screenkode.com", or "app.screenkode.com and app.chabaddigital.com". */
export function appHosts(apps: OrganizationAppInfo[], joiner: 'and' | 'or' = 'and'): string {
  return listOf(apps.map((app) => app.host), joiner);
}
